import * as z from "zod";
import { userFacingError } from "../authorization";
import { isUniqueConstraintViolation } from "../dbErrors";
import { normalizeName } from "../normalizeName";
import {
  assertCustomRolePermissions,
  PERMISSIONS,
  SCOPE_KINDS,
} from "../rbac/permissions";
import { mutator } from "./mutator";

export const MAX_ROLE_NAME_LENGTH = 50;

const roleNameField = z
  .string()
  .min(1, { message: "Role name is required" })
  .max(MAX_ROLE_NAME_LENGTH, {
    message: `Role name must be ${MAX_ROLE_NAME_LENGTH} characters or less`,
  })
  .transform(normalizeName)
  .refine((n) => n.length > 0, {
    message: "Role name must contain at least one visible character",
  });

export const createRole = mutator({
  schema: z.object({
    id: z.string(),
    name: roleNameField,
    scopeKind: z.enum(SCOPE_KINDS),
    permissions: z.array(z.enum(PERMISSIONS)).min(1),
  }),
  resolve: {},
  permissions: [
    { permission: "tenant:roles_manage", scopeId: ({ ctx }) => ctx.tenantId },
  ],
  fn: async ({ tx, ctx, args }) => {
    assertCustomRolePermissions(args.scopeKind, args.permissions);
    try {
      await tx.mutate.roles.insert({
        id: args.id,
        name: args.name,
        scopeKind: args.scopeKind,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        tenantId: ctx.tenantId,
      });
    } catch (e) {
      if (isUniqueConstraintViolation(e)) {
        throw userFacingError("A role with this name already exists");
      }
      throw e;
    }
    for (const permission of new Set(args.permissions)) {
      await tx.mutate.rolePermissions.insert({
        roleId: args.id,
        permission,
        tenantId: ctx.tenantId,
      });
    }
  },
});
